import * as React from "react";
import * as moment from "moment";
import {
    Policy,
    POLICY_STATUS_MTC,
    POLICY_STATUS_PENDING_RENEWAL,
    Renewal,
    RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL,
} from "@ferry/journey-services";
import {ImgTileLink} from "../../../../../components/ImgTileLink";
import {Link} from "react-router-dom";
import {getBrandFromPolicyNumber, WarningBanner, resourcesHelper} from "@ferry/portal-core";

interface IAnnualTilesProps {
    policies?: Policy[];
    renewalProposal?: Renewal;
    policyNumber: string;
    showOldPoliciesTile: boolean;
    hasMultiplePolicies?: boolean;
    renewalEnabled: boolean;
}

function prepareFlags(policies: Policy[] | undefined, renewalProposal: Renewal | undefined) {
    const hasPolicies = policies && policies.length > 0;
    const hasPendingRenewal: boolean = renewalProposal != null && renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;
    const policy: Policy | null = policies && policies.length > 0 ? policies[0] : null;
    const hasActivePolicy: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSameOrAfter(moment(), 'day');
    const policyCanceled = policy != null && policy.status === POLICY_STATUS_MTC;
    const renewalStatus: object = {
        "status": hasPendingRenewal ? "Existing" : undefined
    };
    const inGracePeriod: boolean = policy?.annualPolicyInfo ? policy.annualPolicyInfo.inGracePeriod : false;
    const futureCancelledPolicyBeforeInception: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSame(moment.utc(policy.startDate, moment.ISO_8601))
        && moment.utc(policy.startDate, moment.ISO_8601).isAfter(moment(), 'day');
    return {
        hasPolicies,
        hasPendingRenewal,
        policy,
        hasActivePolicy,
        policyCanceled,
        renewalStatus,
        inGracePeriod,
        futureCancelledPolicyBeforeInception
    };
}

export const AnnualTiles: React.FC<IAnnualTilesProps> = (props) => {
    const {
        policies,
        showOldPoliciesTile,
        renewalProposal,
        policyNumber,
        hasMultiplePolicies,
        renewalEnabled,
    } = props;

    const {
        hasPolicies,
        hasPendingRenewal,
        policy,
        hasActivePolicy,
        policyCanceled,
        renewalStatus,
        inGracePeriod,
        futureCancelledPolicyBeforeInception
    } = prepareFlags(policies, renewalProposal);

    const brandName: string = policyNumber ?
        getBrandFromPolicyNumber(policyNumber)! :
        resourcesHelper.THEME_NAME;

    const cancelPolicyUrl: string = (!hasPendingRenewal || !renewalEnabled) && policy != null
        ? '/journey/init/product/AnnualMotorInsurance/process/mtc/presetPage/' + policy.interaction!._id
        : '/journey/init/product/AnnualMotorInsurance/process/renewal/renewalMTCEntryPage/' + renewalProposal!._id;

    return (
        <div className="link-grid">
            <div className="d-flex flex-wrap">

                {hasPendingRenewal && (
                    <div className="flexItem pending-renewal">
                        <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/policy-summary.svg`}
                                     alt="Policy renewal icon"
                                     to={RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL + renewalProposal!._id}
                                     status="New"
                                     hasInfoButton={true}
                                     linkDisabled={inGracePeriod}
                                     backButtonName={inGracePeriod ? "Cancel" : "Back"}
                                     infoModalButtonHidden={inGracePeriod}
                                     infoModalTitle="Your renewal, make changes"
                                     infoModalAdditionalDescription={inGracePeriod ?
                                         <WarningBanner wording={
                                             <>
                                                 <p className="font-weight-bold">You are no longer covered.</p>
                                                 You must renew your car policy before making changes to your cover.
                                                 <Link className="btn btn-white-dashboard mb-0 mt-0"
                                                       id="payNow"
                                                       to={RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL + renewalProposal!._id}
                                                 >
                                                     Renew your policy now
                                                 </Link>
                                             </>
                                         }/> : ""}
                                     infoModalBulletPoints={["Make changes to your policy for next year (e.g. add a driver).",
                                         "Choose to pay in one go or monthly (if available).",
                                         "Add or remove optional extras for next year.",
                                         "Review your level of cover and compare options (if eligible)."]}
                                     {...props}>
                            Your renewal, make changes
                        </ImgTileLink>
                    </div>
                )}

                {hasActivePolicy && !futureCancelledPolicyBeforeInception && !policyCanceled && (
                    <div className="flexItem policy-summary">
                        <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/policy-summary.svg`}
                                     alt="Policy summary icon"
                                     to={`/dashboard/annualMotor/policySummary/${policyNumber}`}
                                     hasInfoButton={true}
                                     infoModalTitle="Your car policy"
                                     infoModalBulletPoints={["View a short breakdown of your car policy"]}
                                     infoModalButton="Policy summary"
                                     {...renewalStatus}
                                     {...props}>
                            Policy summary <span className="arrow-right"/>
                        </ImgTileLink>
                    </div>
                )}

                {!inGracePeriod && hasPolicies && !policyCanceled && (
                    <div className="flexItem car">
                        <ImgTileLink id="carTile"
                                     src={`/${resourcesHelper.THEME_PATH}/img/car-icon-panel.svg`}
                                     alt="Car icon"
                                     to={`/dashboard/annualMotor/yourCars/${policyNumber}`}
                                     hasInfoButton={true}
                                     infoModalTitle="Your car, make changes"
                                     infoModalBulletPoints={["Change your car or edit the details", "Edit driver details",
                                         "Add/remove additional drivers", "Make changes to your policy (e.g. Business user, mileage etc.)",
                                         "Add or remove optional extras"]}
                                     infoModalAdditionalDescription={
                                         <h4 className="fs-16 mb-3 font-weight-400">
                                             With {brandName}, you're in control. Because you make changes to your policy
                                             yourself,
                                             there are <strong>no admin fees</strong>! Remember, though, any changes you
                                             make can still change your
                                             price.
                                         </h4>}
                                     {...renewalStatus}
                                     {...props}>
                            Your car, make changes <span className="arrow-right"/>
                        </ImgTileLink>
                    </div>
                )}

                {hasPolicies && <div className="flexItem payment">
                    <ImgTileLink id="paymentTile"
                                 src={`/${resourcesHelper.THEME_PATH}/img/payment-wallet.svg`}
                                 alt="Payment icon"
                                 to={`/dashboard/annualPaymentHistory/${policyNumber}`}
                                 notification={hasPolicies && !policyCanceled}
                                 hasInfoButton={true}
                                 infoModalTitle="Payment"
                                 infoModalBulletPoints={["Change your payment day", "Edit your monthly payment details",
                                     "Change your payment card", "Pay off your balance",
                                     "View payment history"]}
                                 {...props}>
                        Payment<span className="arrow-right"/>
                    </ImgTileLink>
                </div>}

                {hasPolicies && <div className="flexItem documents">
                    <ImgTileLink id="policyDocumentsTile"
                                 src={`/${resourcesHelper.THEME_PATH}/img/documents.svg`}
                                 alt="Documents icon"
                                 to={`/dashboard/annualMotorDocuments/${policyNumber}`}
                                 hasInfoButton={true}
                                 infoModalTitle="Documents"
                                 infoModalBulletPoints={["View your policy documents", "View your old policy documents",
                                     "Request documents to be sent by post"]}
                                 {...props}>
                        {(policyCanceled || !hasActivePolicy) ? "Archived documents" : "Documents"} <span className="arrow-right"/>
                    </ImgTileLink>
                </div>}

                {hasPolicies && <div className="flexItem make-a-claim">
                    <ImgTileLink id="claimsTile"
                                 src={`/${resourcesHelper.THEME_PATH}/img/make-a-claim.svg`}
                                 alt="Make a claim icon"
                                 to={`/dashboard/pickMotorClaimKind/${policyNumber}`}
                                 hasInfoButton={true}
                                 infoModalTitle="Make a claim"
                                 infoModalBulletPoints={["Make a windscreen or window claim",
                                     "Make other claims (road traffic accident, theft, misfuelling, lost/stolen keys, etc."]}
                                 {...props}>
                        Make a claim <span className="arrow-right"/>
                    </ImgTileLink>
                </div>}

                <div className="flexItem quotes">
                    <ImgTileLink id="getQuoteTile"
                                 src={`/${resourcesHelper.THEME_PATH}/img/my-quote.svg`}
                                 alt="Quotes icon"
                                 to={`/dashboard/getNewQuote`}
                                 routeState={{
                                     policy,
                                 }}
                                 hasInfoButton={true}
                                 infoModalTitle={"My quote(s)"}
                                 infoModalBulletPoints={["View your car insurance quote", "Get a new car insurance quote"]}
                                 infoModalButton="Get a quote"
                                 {...props}>
                        My quote(s) <span className="arrow-right"/>
                    </ImgTileLink>
                </div>

                {hasPolicies && !hasMultiplePolicies && <div className="flexItem account-details">
                    <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/account-details.svg`}
                                 alt="Account details icon"
                                 to={`/dashboard/annualMotorAccount/${policyNumber}`}
                                 hasInfoButton={true}
                                 infoModalTitle="Account details"
                                 infoModalBulletPoints={["View and amend account holder details (e.g. email, phone number, address, password)",
                                     "Opt in to auto-renew your policy"]}
                                 {...props}>
                        Account details <span className="arrow-right"/>
                    </ImgTileLink>
                </div>}

                <div className="flexItem help">
                    <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/help.svg`}
                                 alt="Help icon"
                                 to={`/dashboard/help/${policyNumber}`}
                                 openInNewWindow={false}
                                 hasInfoButton={true}
                                 infoModalTitle="Help"
                                 infoModalBulletPoints={["View 'How to' guides",
                                     "Read FAQs about your cover, account, payment, breakdown, renewal, claims, NCD..."]}
                                 {...props}>
                        Help <span className="arrow-right"/>
                    </ImgTileLink>
                </div>

                {!inGracePeriod && hasPolicies && !policyCanceled && (<div className="flexItem cancel-policy">
                        <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/cancel-policy.svg`}
                                     alt="Cancel policy"
                                     to={cancelPolicyUrl}
                                     routeState={{
                                         policy,
                                         renewalProposal
                                     }}
                                     hasInfoButton={true}
                                     infoModalTitle="Cancel policy"
                                     infoModalBulletPoints={["Cancel your policy"]}
                                     {...props}>
                            Cancel policy <span className="arrow-right"/>
                        </ImgTileLink>
                    </div>
                )}

                {showOldPoliciesTile && (<div className="flexItem old-policies">
                        <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/old-policies.svg`}
                                     alt="Old policies"
                                     to={"/dashboard/oldPolicies"}
                                     hasInfoButton={true}
                                     infoModalTitle="Old policies"
                                     infoModalBulletPoints={["View your previous policies",
                                         "Make a claim on your previous policies"]}
                                     {...props}>
                            Old policies <span className="arrow-right"/>
                        </ImgTileLink>
                    </div>
                )}

                {hasPolicies && !hasMultiplePolicies && <div className="flexItem marketing-preferences">
                    <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/marketing-preferences.svg`}
                                 alt="Marketing preferences"
                                 to={"/dashboard/account/marketingPreferences"}
                                 hasInfoButton={true}
                                 infoModalTitle="Marketing preferences"
                                 infoModalBulletPoints={["Change your marketing preferences (hear about our latest products, services and discounts"]}
                                 {...props}>
                        Marketing preferences <span className="arrow-right"/>
                    </ImgTileLink>
                </div>}

            </div>
        </div>
    );
}
